import React, { useState } from "react";
import { User, Lock } from "lucide-react";

interface TransferOwnershipModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (newOwnerId: string) => void;
  currentOwnerEmail: string;
  organizationName: string;
  selectedMemberEmail: string;
  selectedMemberId: string;
}

const TransferOwnershipModal: React.FC<TransferOwnershipModalProps> = ({
  open,
  onClose,
  onConfirm,
  currentOwnerEmail,
  organizationName,
  selectedMemberEmail,
  selectedMemberId,
}) => {
  const [step, setStep] = useState<"initial" | "confirm">("initial");

  const handlePrimaryConfirm = () => {
    setStep("confirm");
  };

  const handleFinalConfirm = () => {
    onConfirm(selectedMemberId);
  };

  const handleBackToInitial = () => {
    setStep("initial");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-background-paper dark:bg-background-paperDark p-6 rounded-lg shadow-lg w-full max-w-lg">
        {step === "initial" && (
          <>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-bold text-text-primary dark:text-text-primaryDark">
                Confirm Ownership Transfer
              </h2>
              <button
                onClick={onClose}
                aria-label="Close"
                className="text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition"
              >
                ✕
              </button>
            </div>

            <div className="flex items-center mb-6 bg-background-panel dark:bg-background-panelDark rounded-lg p-4">
              <User className="w-12 h-12 text-secondary mr-4" />
              <div>
                <p className="text-lg font-semibold text-text-primary dark:text-text-primaryDark">
                  {selectedMemberEmail}
                </p>
              </div>
            </div>

            <p className="text-sm text-text-secondary dark:text-text-secondaryDark mb-6">
              Are you sure you want to transfer ownership of{" "}
              <strong className="text-text-primary dark:text-text-primaryDark">
                {organizationName}
              </strong>{" "}
              from{" "}
              <strong className="text-text-primary dark:text-text-primaryDark">
                {currentOwnerEmail}
              </strong>{" "}
              to{" "}
              <strong className="text-text-primary dark:text-text-primaryDark">
                {selectedMemberEmail}
              </strong>
              ? This action cannot be undone.
            </p>

            <div className="flex justify-end space-x-4">
              <button
                onClick={onClose}
                className="py-2 px-6 rounded-md text-sm font-medium border border-divider dark:border-divider-dark text-text-secondary dark:text-text-secondaryDark hover:bg-background-panel dark:hover:bg-background-panelDark transition"
              >
                Cancel
              </button>
              <button
                onClick={handlePrimaryConfirm}
                className="py-2 px-6 rounded-md text-sm font-medium bg-secondary text-white hover:bg-secondary-dark transition"
              >
                Confirm Transfer
              </button>
            </div>
          </>
        )}

        {step === "confirm" && (
          <>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-bold text-text-primary dark:text-text-primaryDark">
                Final Confirmation
              </h2>
              <button
                onClick={onClose}
                aria-label="Close"
                className="text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition"
              >
                ✕
              </button>
            </div>

            <div className="flex items-center mb-6 bg-background-panel dark:bg-background-panelDark rounded-lg p-4">
              <Lock className="w-12 h-12 text-secondary mr-4" />
              <div>
                <p className="text-lg font-semibold text-text-primary dark:text-text-primaryDark">
                  Please confirm this sensitive action.
                </p>
              </div>
            </div>

            <p className="text-sm text-text-secondary dark:text-text-secondaryDark mb-6">
              By proceeding, you will transfer ownership of{" "}
              <strong className="text-text-primary dark:text-text-primaryDark">
                {organizationName}
              </strong>{" "}
              to{" "}
              <strong className="text-text-primary dark:text-text-primaryDark">
                {selectedMemberEmail}
              </strong>
              . This action cannot be undone, and your role will be changed to{" "}
              <strong className="text-secondary">Super Admin</strong>.
            </p>

            <div className="flex justify-between space-x-4">
              <button
                onClick={handleBackToInitial}
                className="py-2 px-6 rounded-md text-sm font-medium border border-divider dark:border-divider-dark text-text-secondary dark:text-text-secondaryDark hover:bg-background-panel dark:hover:bg-background-panelDark transition"
              >
                Back
              </button>
              <button
                onClick={handleFinalConfirm}
                className="py-2 px-6 rounded-md text-sm font-medium bg-secondary text-white hover:bg-secondary-dark transition"
              >
                Confirm Transfer
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TransferOwnershipModal;
